import { cx, styled } from "simplydo/react/styled";

import type { TableSize, TextAlign, VerticalAlign } from "./Table.types";

export type TableStyleProps = {
  $celled?: boolean | "internally";
  $compact?: boolean | "very";
  $basic?: boolean | "very";
  $padded?: boolean | "very";
  $size?: TableSize;
  $fixed?: boolean;
  $textAlign?: TextAlign;
  $verticalAlign?: VerticalAlign;
};

export type CellStyleProps = { $textAlign?: TextAlign; $widthPct?: string };

function widthPct(width?: number | string): string | undefined {
  if (width == null) return undefined;
  const n = Number(width);
  if (!Number.isFinite(n) || n <= 0) return undefined;
  return `${(n / 16) * 100}%`;
}

export { widthPct };

export const StyledTable = styled.table<TableStyleProps>`
  width: 100%;
  margin: 1rem 0;
  background: ${({ $basic }) => ($basic ? "transparent" : "#fff")};
  border: ${({ $basic }) =>
    $basic === "very" ? "none" : "1px solid rgba(34, 36, 38, 0.15)"};
  border-radius: ${({ $basic }) => ($basic ? "0" : "0.28571429rem")};
  box-shadow: ${({ $basic }) =>
    $basic ? "none" : "0 1px 2px 0 rgba(34, 36, 38, 0.15)"};
  border-collapse: separate;
  border-spacing: 0;
  text-align: ${({ $textAlign }) => $textAlign || "left"};
  vertical-align: ${({ $verticalAlign }) => $verticalAlign || "middle"};
  color: rgba(0, 0, 0, 0.87);
  font-family: inherit;
  font-size: ${({ $size }) =>
    $size === "small" ? "0.9em" : $size === "large" ? "1.1em" : "1em"};
  table-layout: ${({ $fixed }) => ($fixed ? "fixed" : "auto")};

  &.is-collapsing {
    width: auto;
  }

  th,
  td {
    border-left: ${({ $celled, $basic }) => {
      if ($basic === "very") return "none";
      if ($celled) return "1px solid rgba(34, 36, 38, 0.1)";
      return "none";
    }};
    border-top: ${({ $basic }) =>
      $basic === "very" ? "none" : "1px solid rgba(34, 36, 38, 0.1)"};
    vertical-align: ${({ $verticalAlign }) => $verticalAlign || "middle"};
    transition: background 0.1s ease;
  }

  th {
    padding: ${({ $compact, $padded }) => {
      if ($compact === "very") return "1em 0.6em";
      if ($compact) return "1em 0.7em";
      if ($padded === "very") return "1.5em 1em";
      if ($padded) return "1em";
      return "1em 1em";
    }};
  }

  tbody:first-child tr:first-child td {
    border-top: none;
  }

  td {
    padding: ${({ $compact, $padded }) => {
      if ($compact === "very") return "0.4em 0.6em";
      if ($compact) return "0.6em";
      if ($padded === "very") return "1.5em 1em";
      if ($padded) return "1em";
      return "0.7em";
    }};
  }

  th:first-child,
  td:first-child {
    border-left: none;
  }

  thead th {
    border-top: none;
    background: ${({ $basic }) =>
      $basic ? "transparent" : "rgba(0, 0, 0, 0.02)"};
    font-weight: 700;
    color: rgba(0, 0, 0, 0.87);
  }

  &.basic {
    border: none;
  }

  &.is-sortable thead th {
    cursor: pointer;
    user-select: none;
  }

  &.is-striped tbody tr:nth-child(2n) {
    background: rgba(0, 0, 50, 0.02);
  }

  &.is-selectable tbody tr {
    cursor: pointer;
  }

  &.is-selectable tbody tr:hover {
    background: rgba(0, 0, 0, 0.05) !important;
  }

  &.is-single-line th,
  &.is-single-line td {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &.is-definition td:first-child,
  &.is-definition th:first-child {
    font-weight: 700;
    background: rgba(0, 0, 0, 0.03);
  }
`;

export const StyledCell = styled.td<CellStyleProps>`
  text-align: ${({ $textAlign }) => $textAlign || "inherit"};
  width: ${({ $widthPct }) => $widthPct};

  &.vertical-align-top {
    vertical-align: top;
  }
  &.vertical-align-bottom {
    vertical-align: bottom;
  }
  &.vertical-align-middle {
    vertical-align: middle;
  }
  &.vertical-align-top {
    vertical-align: top;
  }

  &.is-collapsing {
    width: 1%;
    white-space: nowrap;
  }

  &.is-single-line {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &.is-active {
    background: rgba(0, 0, 0, 0.05) !important;
  }

  &.is-positive {
    background: #fcfff5 !important;
    color: #2c662d;
  }

  &.is-negative {
    background: #fff6f6 !important;
    color: #9f3a38;
  }

  &.is-warning {
    background: #fffaf3 !important;
    color: #573a08;
  }

  &.is-error {
    background: #fff6f6 !important;
    color: #9f3a38;
  }

  &.is-disabled {
    opacity: 0.45;
    pointer-events: none;
  }

  &.is-selectable {
    cursor: pointer;
  }

  &.is-selectable:hover {
    background: rgba(0, 0, 0, 0.05);
  }
`;

export const StyledHeaderCell = styled.th<CellStyleProps>`
  text-align: ${({ $textAlign }) => $textAlign || "inherit"};
  width: ${({ $widthPct }) => $widthPct};

  &.is-collapsing {
    width: 1%;
    white-space: nowrap;
  }

  &.is-single-line {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &.is-active {
    background: rgba(0, 0, 0, 0.05) !important;
  }

  &.is-positive {
    background: #fcfff5 !important;
    color: #2c662d;
  }

  &.is-negative {
    background: #fff6f6 !important;
    color: #9f3a38;
  }

  &.is-warning {
    background: #fffaf3 !important;
    color: #573a08;
  }

  &.is-error {
    background: #fff6f6 !important;
    color: #9f3a38;
  }

  &.is-disabled {
    opacity: 0.45;
    pointer-events: none;
  }
`;

export const StyledRow = styled.tr<{
  $textAlign?: TextAlign;
  $verticalAlign?: VerticalAlign;
}>`
  text-align: ${({ $textAlign }) => $textAlign || "inherit"};
  vertical-align: ${({ $verticalAlign }) => $verticalAlign || "inherit"};

  &.is-active {
    background: rgba(0, 0, 0, 0.05) !important;
  }

  &.is-positive {
    background: #fcfff5 !important;
  }

  &.is-negative,
  &.is-error {
    background: #fff6f6 !important;
  }

  &.is-warning {
    background: #fffaf3 !important;
  }

  &.is-disabled {
    opacity: 0.45;
    pointer-events: none;
  }
`;
